// src/components/maps/admin/GeoReferenceEditor.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Paper,
  Typography,
  Slider,
  Button,
  TextField,
  Grid,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Satellite as SatelliteIcon,
  Map as MapIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  OpenWith as MoveIcon,
  LockOpen as UnlockIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import indoorMappingService from '../../../services/indoorMappingService';

// Default location: Vinktstraat 1, 9800 Deinze, Belgium
const DEFAULT_CENTER = [50.9847, 3.5278];
const DEFAULT_ZOOM = 19;

/**
 * Custom rotatable image overlay using CSS transforms
 * This approach allows rotation which standard Leaflet ImageOverlay doesn't support
 */
const RotatableImageOverlay = ({
  url,
  center,
  scale,
  rotation,
  opacity,
  locked,
  onTransformChange,
}) => {
  const map = useMap();
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ width: 800, height: 600 });

  // Create container and image elements once
  useEffect(() => {
    if (!map || !url) return;

    // Create a custom pane for the floor plan
    if (!map.getPane('floorPlanPane')) {
      const pane = map.createPane('floorPlanPane');
      pane.style.zIndex = 450;
    }

    // Create container
    const container = document.createElement('div');
    container.className = 'floor-plan-container';
    container.style.position = 'absolute';
    container.style.pointerEvents = 'auto';
    containerRef.current = container;
    map.getPane('floorPlanPane').appendChild(container);

    // Create image
    const img = document.createElement('img');
    img.crossOrigin = 'anonymous';
    img.style.display = 'block';
    img.style.transformOrigin = 'center center';
    img.onload = () => {
      setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
      setImageLoaded(true);
      console.log('[RotatableImageOverlay] Image loaded:', img.naturalWidth, 'x', img.naturalHeight);
    };
    img.onerror = (e) => {
      console.error('[RotatableImageOverlay] Image load error:', e);
    };
    img.src = url;
    imgRef.current = img;
    container.appendChild(img);

    return () => {
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
      containerRef.current = null;
      imgRef.current = null;
      setImageLoaded(false);
    };
  }, [map, url]);

  // Update visual properties when props change
  useEffect(() => {
    if (!map || !containerRef.current || !imgRef.current || !imageLoaded) return;

    const container = containerRef.current;
    const img = imgRef.current;

    // Calculate display size
    const displayWidth = naturalSize.width * scale;
    const displayHeight = naturalSize.height * scale;

    // Get pixel position for center
    const pixelCenter = map.latLngToLayerPoint(center);

    // Update image styles
    img.style.width = `${displayWidth}px`;
    img.style.height = `${displayHeight}px`;
    img.style.opacity = String(opacity);

    // Position container centered on the geo point
    container.style.left = `${pixelCenter.x - displayWidth / 2}px`;
    container.style.top = `${pixelCenter.y - displayHeight / 2}px`;
    container.style.width = `${displayWidth}px`;
    container.style.height = `${displayHeight}px`;
    container.style.transform = `rotate(${rotation}deg)`;
    container.style.transformOrigin = 'center center';
    container.style.cursor = locked ? 'default' : 'move';

  }, [map, center, scale, rotation, opacity, locked, imageLoaded, naturalSize]);

  // Update position on map move/zoom
  useMapEvents({
    move: () => {
      if (!map || !containerRef.current || !imgRef.current || !imageLoaded) return;
      const pixelCenter = map.latLngToLayerPoint(center);
      const displayWidth = naturalSize.width * scale;
      const displayHeight = naturalSize.height * scale;
      containerRef.current.style.left = `${pixelCenter.x - displayWidth / 2}px`;
      containerRef.current.style.top = `${pixelCenter.y - displayHeight / 2}px`;
    },
    zoom: () => {
      if (!map || !containerRef.current || !imgRef.current || !imageLoaded) return;
      const pixelCenter = map.latLngToLayerPoint(center);
      const displayWidth = naturalSize.width * scale;
      const displayHeight = naturalSize.height * scale;
      containerRef.current.style.left = `${pixelCenter.x - displayWidth / 2}px`;
      containerRef.current.style.top = `${pixelCenter.y - displayHeight / 2}px`;
    },
  });

  // Handle dragging
  useEffect(() => {
    if (!containerRef.current || !map) return;

    const container = containerRef.current;

    const onMouseDown = (e) => {
      if (locked) return;
      isDragging.current = true;
      dragStart.current = { x: e.clientX, y: e.clientY };
      map.dragging.disable();
      e.stopPropagation();
      e.preventDefault();
    };

    const onMouseMove = (e) => {
      if (!isDragging.current) return;

      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;

      // Convert pixel delta to lat/lng delta
      const currentPixel = map.latLngToContainerPoint(center);
      const newPixel = L.point(currentPixel.x + dx, currentPixel.y + dy);
      const newLatLng = map.containerPointToLatLng(newPixel);

      onTransformChange({
        center: [newLatLng.lat, newLatLng.lng],
      });

      dragStart.current = { x: e.clientX, y: e.clientY };
    };

    const onMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        map.dragging.enable();
      }
    };

    container.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [map, center, locked, onTransformChange]);

  return null;
};

/**
 * Component to capture map reference
 */
const MapRefCapture = ({ onMapRef }) => {
  const map = useMap();
  useEffect(() => {
    if (map && onMapRef) {
      onMapRef(map);
    }
  }, [map, onMapRef]);
  return null;
};

/**
 * Geo-reference editor for aligning floor plan to satellite map
 */
const GeoReferenceEditor = ({ floorPlan, onSave }) => {
  // Debug: Log the floor plan data received
  console.log('[GeoReferenceEditor] Received floorPlan:', {
    id: floorPlan?.id,
    bounds_north: floorPlan?.bounds_north,
    bounds_south: floorPlan?.bounds_south,
    bounds_east: floorPlan?.bounds_east,
    bounds_west: floorPlan?.bounds_west,
    rotation_degrees: floorPlan?.rotation_degrees,
    scale_meters_per_pixel: floorPlan?.scale_meters_per_pixel,
    is_geo_referenced: floorPlan?.is_geo_referenced,
  });

  // Handle both snake_case (API) and camelCase field names
  const imageUrl = floorPlan.image_url || floorPlan.imageUrl;
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

  // Build full image URL
  const fullImageUrl = imageUrl
    ? (imageUrl.startsWith('http') ? imageUrl : `${API_BASE_URL}${imageUrl}`)
    : null;

  // Get existing bounds/transform from floor plan
  const existingBounds = {
    north: floorPlan.bounds_north || floorPlan.bounds?.north,
    south: floorPlan.bounds_south || floorPlan.bounds?.south,
    east: floorPlan.bounds_east || floorPlan.bounds?.east,
    west: floorPlan.bounds_west || floorPlan.bounds?.west,
  };
  const existingRotation = parseFloat(floorPlan.rotation_degrees || floorPlan.rotation || 0) || 0;
  const existingScale = parseFloat(floorPlan.scale_meters_per_pixel) || null;

  // Calculate center and scale from bounds if available
  // Note: We need image dimensions to properly calculate scale, so we defer that
  const getInitialTransform = (imgWidth = 800, imgHeight = 600) => {
    const north = parseFloat(existingBounds.north);
    const south = parseFloat(existingBounds.south);
    const east = parseFloat(existingBounds.east);
    const west = parseFloat(existingBounds.west);

    if (!isNaN(north) && !isNaN(south) && !isNaN(east) && !isNaN(west) &&
        north !== 0 && south !== 0 && east !== 0 && west !== 0) {
      const centerLat = (north + south) / 2;
      const centerLng = (east + west) / 2;

      // Reverse the bounds calculation to get scale
      // heightMeters = latDiff * 111000
      // scale = heightMeters / imgHeight
      const latDiff = north - south;
      const heightMeters = latDiff * 111000;
      const approxScale = heightMeters / imgHeight;

      console.log('[GeoReferenceEditor] getInitialTransform from bounds:', {
        existingBounds: { north, south, east, west },
        centerLat, centerLng,
        latDiff, heightMeters, approxScale,
        imgWidth, imgHeight
      });

      return {
        center: [centerLat, centerLng],
        scale: Math.max(approxScale, 0.01), // Minimum scale
      };
    }
    return {
      center: DEFAULT_CENTER,
      scale: 1.0, // 1.0 = display at natural pixel size
    };
  };

  const initialTransform = getInitialTransform();

  // Ensure center is always valid
  const safeCenter = (c) => {
    if (Array.isArray(c) && c.length === 2 && !isNaN(c[0]) && !isNaN(c[1])) {
      return c;
    }
    return DEFAULT_CENTER;
  };

  // Transform state
  const [center, setCenter] = useState(safeCenter(initialTransform.center));
  const [scale, setScale] = useState(initialTransform.scale || 1.0);
  const [rotation, setRotation] = useState(existingRotation);
  const [opacity, setOpacity] = useState(0.7);
  const [locked, setLocked] = useState(false);

  // UI state
  const [mapType, setMapType] = useState('satellite');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Handle transform changes from overlay
  const handleTransformChange = useCallback((changes) => {
    if (changes.center) {
      const newCenter = changes.center;
      if (Array.isArray(newCenter) && newCenter.length === 2 && !isNaN(newCenter[0]) && !isNaN(newCenter[1])) {
        setCenter(newCenter);
      }
    }
    if (changes.scale !== undefined && !isNaN(changes.scale)) setScale(changes.scale);
    if (changes.rotation !== undefined && !isNaN(changes.rotation)) setRotation(changes.rotation);
  }, []);

  // Rotation controls
  const rotateBy = (degrees) => {
    setRotation((prev) => (Number(prev) + degrees) % 360);
  };

  // Scale controls - scale is a direct display multiplier
  const scaleBy = (factor) => {
    setScale((prev) => Math.max(0.01, Math.min(5, Number(prev) * factor)));
  };

  // Store image dimensions for bounds calculation
  const [imageDimensions, setImageDimensions] = useState({ width: 800, height: 600 });
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load image dimensions when URL changes
  useEffect(() => {
    if (fullImageUrl) {
      setImageLoaded(false);
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        setImageLoaded(true);
        console.log('[GeoReferenceEditor] Image dimensions:', img.naturalWidth, 'x', img.naturalHeight);

        // Recalculate transform from bounds using actual image dimensions
        const north = parseFloat(existingBounds.north);
        const south = parseFloat(existingBounds.south);
        const east = parseFloat(existingBounds.east);
        const west = parseFloat(existingBounds.west);

        if (!isNaN(north) && !isNaN(south) && !isNaN(east) && !isNaN(west) &&
            north !== 0 && south !== 0 && east !== 0 && west !== 0) {
          const centerLat = (north + south) / 2;
          const centerLng = (east + west) / 2;

          // Calculate display scale from bounds and image dimensions
          // This is the reverse of what we do when saving
          // heightMeters = latDiff * 111000
          // displayScale = heightMeters / (naturalHeight * metersPerPixelAtZoom19)
          // But simpler: we want the image to appear at the right size when positioned
          // Using the bounds, we can calculate what display scale was used
          const latDiff = north - south;
          const heightMeters = latDiff * 111000;

          // At the map zoom level, calculate what scale makes the image fit the bounds
          // scale_meters_per_pixel tells us how many meters each source pixel represents
          // But for display, we need to figure out the multiplier that was used
          // The relationship: displayHeight (pixels on screen) corresponds to heightMeters
          // If we know metersPerPixelAtZoom19 ≈ 0.3, then:
          // displayHeight = heightMeters / 0.3
          // displayScale = displayHeight / naturalHeight
          const metersPerPixelAtZoom19 = 0.3;
          const displayHeight = heightMeters / metersPerPixelAtZoom19;
          const calculatedDisplayScale = displayHeight / img.naturalHeight;

          // Clamp to reasonable range (allow very small scales for large floor plans)
          const finalScale = Math.max(0.01, Math.min(5, calculatedDisplayScale));

          console.log('[GeoReferenceEditor] Calculated display scale from bounds:', {
            latDiff,
            heightMeters,
            naturalHeight: img.naturalHeight,
            calculatedDisplayScale,
            finalScale,
            savedScaleMetersPerPixel: existingScale
          });

          console.log('[GeoReferenceEditor] Restoring geo-reference:', {
            center: [centerLat, centerLng],
            scale: finalScale,
            rotation: existingRotation
          });

          setCenter([centerLat, centerLng]);
          setScale(finalScale);
        }
      };
      img.src = fullImageUrl;
    }
  }, [fullImageUrl]); // Only run when URL changes

  // Store map reference for bounds calculation
  const [mapRef, setMapRef] = useState(null);

  // Convert current transform to bounds for saving
  const calculateBounds = () => {
    // Ensure we have valid center coordinates
    const validCenter = safeCenter(center);
    const validScale = Number(scale) || 1.0;

    // Calculate display dimensions in pixels
    const displayWidth = imageDimensions.width * validScale;
    const displayHeight = imageDimensions.height * validScale;

    // If we have a map reference, use it to calculate geo bounds from pixel dimensions
    if (mapRef) {
      const centerPoint = mapRef.latLngToContainerPoint(validCenter);

      // Calculate corner points in container coordinates
      const topLeft = L.point(centerPoint.x - displayWidth / 2, centerPoint.y - displayHeight / 2);
      const bottomRight = L.point(centerPoint.x + displayWidth / 2, centerPoint.y + displayHeight / 2);

      // Convert back to lat/lng
      const topLeftLatLng = mapRef.containerPointToLatLng(topLeft);
      const bottomRightLatLng = mapRef.containerPointToLatLng(bottomRight);

      const bounds = {
        north: topLeftLatLng.lat,
        south: bottomRightLatLng.lat,
        east: bottomRightLatLng.lng,
        west: topLeftLatLng.lng,
      };

      console.log('[GeoReferenceEditor] calculateBounds (from map):', {
        center: validCenter,
        scale: validScale,
        displayWidth,
        displayHeight,
        bounds
      });

      return bounds;
    }

    // Fallback: estimate using zoom level 19 meters per pixel
    // At zoom 19, roughly 0.3 meters per pixel at equator
    const metersPerPixelAtZoom19 = 0.3;
    const widthMeters = displayWidth * metersPerPixelAtZoom19;
    const heightMeters = displayHeight * metersPerPixelAtZoom19;

    // Convert meters to degrees
    const latOffset = (heightMeters / 2) / 111000;
    const lngOffset = (widthMeters / 2) / (111000 * Math.cos(validCenter[0] * Math.PI / 180));

    const bounds = {
      north: validCenter[0] + latOffset,
      south: validCenter[0] - latOffset,
      east: validCenter[1] + lngOffset,
      west: validCenter[1] - lngOffset,
    };

    console.log('[GeoReferenceEditor] calculateBounds (fallback):', {
      center: validCenter,
      scale: validScale,
      imageDimensions,
      widthMeters,
      heightMeters,
      bounds
    });

    return bounds;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const bounds = calculateBounds();
      const rotationValue = Number(rotation) || 0;

      // Calculate actual scale_meters_per_pixel from geo-bounds and image dimensions
      // This is the conversion factor to convert pixel distances to real-world meters
      const latDiff = bounds.north - bounds.south;
      const lngDiff = bounds.east - bounds.west;

      // Height in meters (1 degree latitude ≈ 111,000 meters)
      const heightMeters = latDiff * 111000;

      // Width in meters (varies by latitude, adjusted for center latitude)
      const centerLat = (bounds.north + bounds.south) / 2;
      const widthMeters = lngDiff * 111000 * Math.cos(centerLat * Math.PI / 180);

      // Calculate scale based on which dimension is more accurate
      // Use the average of both dimensions for best estimate
      const scaleFromHeight = heightMeters / imageDimensions.height;
      const scaleFromWidth = widthMeters / imageDimensions.width;
      const calculatedScale = (scaleFromHeight + scaleFromWidth) / 2;

      // Ensure scale is reasonable (between 0.001 and 10 meters per pixel)
      const scaleMetersPerPixel = Math.max(0.001, Math.min(10, calculatedScale));

      console.log('[GeoReferenceEditor] Saving geo-reference:', {
        floorPlanId: floorPlan.id,
        bounds,
        rotation: rotationValue,
        center: safeCenter(center),
        displayScale: scale,
        imageDimensions,
        heightMeters,
        widthMeters,
        scaleFromHeight,
        scaleFromWidth,
        calculatedScale: scaleMetersPerPixel
      });

      // Save bounds, rotation, and calculated scale
      await indoorMappingService.updateFloorPlan(floorPlan.id, {
        bounds_north: bounds.north,
        bounds_south: bounds.south,
        bounds_east: bounds.east,
        bounds_west: bounds.west,
        rotation_degrees: rotationValue,
        scale_meters_per_pixel: scaleMetersPerPixel,
        is_geo_referenced: true,
      });

      setSuccess(true);

      console.log('[GeoReferenceEditor] Save successful!');

      // Call onSave callback if provided (allows parent to refresh data)
      if (onSave) {
        onSave();
      }
    } catch (err) {
      console.error('[GeoReferenceEditor] Save failed:', err);
      setError(err.message || 'Failed to save geo-reference');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const initial = getInitialTransform();
    setCenter(initial.center);
    setScale(initial.scale);
    setRotation(existingRotation);
    setOpacity(0.7);
  };

  // Debug logging
  useEffect(() => {
    console.log('[GeoReferenceEditor] State:', {
      center,
      scale,
      rotation,
      opacity,
      locked,
      fullImageUrl,
    });
  }, [center, scale, rotation, opacity, locked, fullImageUrl]);

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Geo-Reference Floor Plan
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Position and rotate the floor plan to align with the satellite imagery.
          Use the controls below or drag the floor plan directly on the map.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Map Type Toggle */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" gutterBottom display="block">
              Map Type
            </Typography>
            <ToggleButtonGroup
              value={mapType}
              exclusive
              onChange={(e, value) => value && setMapType(value)}
              size="small"
              fullWidth
            >
              <ToggleButton value="satellite">
                <SatelliteIcon sx={{ mr: 0.5 }} fontSize="small" />
                Satellite
              </ToggleButton>
              <ToggleButton value="street">
                <MapIcon sx={{ mr: 0.5 }} fontSize="small" />
                Street
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Opacity Slider */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" gutterBottom display="block">
              Opacity: {Math.round(opacity * 100)}%
            </Typography>
            <Slider
              value={opacity}
              onChange={(e, value) => setOpacity(value)}
              min={0.1}
              max={1}
              step={0.05}
              size="small"
            />
          </Grid>

          {/* Rotation Control */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" gutterBottom display="block">
              Rotation: {Number(rotation).toFixed(1)}°
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <IconButton size="small" onClick={() => rotateBy(-10)} title="Rotate -10°">
                <RotateLeftIcon />
              </IconButton>
              <IconButton size="small" onClick={() => rotateBy(-0.1)} title="Rotate -0.1°">
                <Typography variant="caption">-</Typography>
              </IconButton>
              <Slider
                value={Number(rotation)}
                onChange={(e, value) => setRotation(Number(value))}
                min={-180}
                max={180}
                step={0.1}
                size="small"
                sx={{ mx: 1 }}
              />
              <IconButton size="small" onClick={() => rotateBy(0.1)} title="Rotate +0.1°">
                <Typography variant="caption">+</Typography>
              </IconButton>
              <IconButton size="small" onClick={() => rotateBy(10)} title="Rotate +10°">
                <RotateRightIcon />
              </IconButton>
            </Stack>
          </Grid>

          {/* Scale Control */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" gutterBottom display="block">
              Scale: {Number(scale).toFixed(2)}x
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <IconButton size="small" onClick={() => scaleBy(0.95)} title="Shrink 5%">
                <ZoomOutIcon />
              </IconButton>
              <Slider
                value={Number(scale)}
                onChange={(e, value) => setScale(Number(value))}
                min={0.01}
                max={5}
                step={0.01}
                size="small"
                sx={{ mx: 1 }}
              />
              <IconButton size="small" onClick={() => scaleBy(1.05)} title="Enlarge 5%">
                <ZoomInIcon />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || !fullImageUrl}
          >
            {saving ? 'Saving...' : 'Save Geo-Reference'}
          </Button>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={saving}
          >
            Reset
          </Button>

          <Tooltip title={locked ? 'Unlock floor plan to move it' : 'Lock floor plan position'}>
            <Button
              variant="outlined"
              color={locked ? 'warning' : 'inherit'}
              startIcon={locked ? <LockIcon /> : <UnlockIcon />}
              onClick={() => setLocked(!locked)}
            >
              {locked ? 'Locked' : 'Unlocked'}
            </Button>
          </Tooltip>
        </Box>

        {/* Status Messages */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Geo-reference saved successfully!
          </Alert>
        )}

        {!fullImageUrl && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No floor plan image found. Please upload a floor plan first.
          </Alert>
        )}
      </Paper>

      {/* Map */}
      <Paper elevation={2} sx={{ height: 600, overflow: 'hidden' }}>
        <MapContainer
          center={safeCenter(center)}
          zoom={DEFAULT_ZOOM}
          style={{ width: '100%', height: '100%' }}
          zoomControl={true}
        >
          {/* Capture map reference */}
          <MapRefCapture onMapRef={setMapRef} />

          {/* Satellite imagery layer */}
          {mapType === 'satellite' && (
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='&copy; Esri'
              maxZoom={21}
            />
          )}

          {/* Street map layer */}
          {mapType === 'street' && (
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
              maxZoom={19}
            />
          )}

          {/* Floor plan overlay */}
          {fullImageUrl && (
            <RotatableImageOverlay
              url={fullImageUrl}
              center={safeCenter(center)}
              scale={Number(scale) || 1.0}
              rotation={Number(rotation) || 0}
              opacity={Number(opacity) || 0.7}
              locked={locked}
              onTransformChange={handleTransformChange}
            />
          )}
        </MapContainer>
      </Paper>

      {/* Instructions */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>Instructions:</strong>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
          <li><strong>Move:</strong> Drag the floor plan image (when unlocked)</li>
          <li><strong>Rotate:</strong> Use the rotation slider or buttons for precise alignment</li>
          <li><strong>Scale:</strong> Adjust the scale slider to match the building size</li>
          <li><strong>Opacity:</strong> Lower opacity to see through and align with satellite</li>
          <li><strong>Pan Map:</strong> Click and drag on empty map areas (not on the floor plan)</li>
        </ul>
      </Alert>
    </Box>
  );
};

export default GeoReferenceEditor;
